import React, { useState, useContext, useEffect } from 'react';
import { Box, Typography, Button, Grid, Card, FormControl, InputLabel, Select, MenuItem, TextField, RadioGroup, Radio, FormControlLabel, CircularProgress, Autocomplete, Chip, OutlinedInput } from '@mui/material';
import { Form, Formik, Field } from 'formik';
import { DispatchContext, StateContext } from '../../../store';
import { SNACKBAR_OPEN } from '../../../reducers';
import { exhibitions, ticketsApi, getTourGuideByCid, getTourOperatorByLicense, getTicketDiscounts, museumExhibitionTypes, getExhibitions, visitorTypes } from '../../../services/TicketsServices';
import { mastersApi } from '../../../services/MasterDataServices';
import countryList from 'react-select-country-list';
import PageWrapper from '../../../common/PageWrapper/PageWrapper';

const TemporaryExhibition = () => {
  const dispatch = useContext(DispatchContext);
  const [showSuccess, setShowSuccess] = useState(false);
  const [exhibitionTypeOptions, setExhibitionTypeOptions] = useState([]);
  const [ticketTypeOptions, setTicketTypeOptions] = useState([]);
  const [visitorTypeOptions, setVisitorTypeOptions] = useState([]);
  const [tourOperatorOptions, setTourOperatorOptions] = useState([]);
  const [guideCid, setGuideCid] = useState('');
  const [tourOperatorLicense, setTourOperatorLicense] = useState('');
  const [ticketDiscounts, setTicketDiscounts] = useState([]);
  const [payableAmount, setPayableAmount] = useState(0);
  const [loadingExhibitionTypes, setLoadingExhibitionTypes] = useState(false);
  const [loadingTicketTypes, setLoadingTicketTypes] = useState(false);
  const countries = countryList().getData();
  const user = JSON.parse(localStorage.getItem("user"));
  const museumId = user?.museums?.[0]?.id;

  const getCurrentMuseumTypeId = () => {
    return user?.museums?.[0]?.museum_type?.id || user?.museum_type_id || null;
  };

  const filterTicketTypesByMuseum = (arr) => {
    const list = arr || [];
    if (list.some((t) => t?.museum_id)) {
      return list.filter((t) => String(t?.museum_id) === String(museumId));
    }
    const currentMuseumTypeId = getCurrentMuseumTypeId();
    if (currentMuseumTypeId && list.some((t) => t?.museum_type?.id)) {
      return list.filter((t) => String(t?.museum_type?.id) === String(currentMuseumTypeId));
    }
    return list;
  };

  
  // Fetch exhibition types
  useEffect(() => {
    let isMounted = true;
    let retryCount = 0;
    const maxRetries = 2;

    const fetchExhibitionTypes = async () => {
      if (!isMounted || retryCount >= maxRetries) {
        if (retryCount >= maxRetries && isMounted) {
          setExhibitionTypeOptions([
            { id: 1, name: 'Temporary Exhibition A' },
            { id: 2, name: 'Temporary Exhibition B' },
            { id: 3, name: 'Special Exhibition' },
            { id: 4, name: 'Weekend Exhibition' }
          ]);
          setLoadingExhibitionTypes(false);
        }
        return;
      }

      setLoadingExhibitionTypes(true);
      try {
        const response = await getExhibitions({ page: 1, per_page: 10 });
        if (isMounted) {
          setExhibitionTypeOptions(response?.data || []);
          setLoadingExhibitionTypes(false);
        }
      } catch (error) {
        console.error('Temporary Exhibition: Failed to fetch exhibition types:', error);
        setExhibitionTypeOptions([]);
        setLoadingExhibitionTypes(false);
        if (retryCount < maxRetries) {
          setTimeout(() => fetchExhibitionTypes(), 2000 * retryCount); // Exponential backoff
        } else {
          if (isMounted) {
            setExhibitionTypeOptions([
              { id: 1, name: 'Temporary Exhibition A' },
              { id: 2, name: 'Temporary Exhibition B' },
              { id: 3, name: 'Special Exhibition' },
              { id: 4, name: 'Weekend Exhibition' }
            ]);
            setLoadingExhibitionTypes(false);
            dispatch({
              type: SNACKBAR_OPEN,
              payload: { isNotify: true, severity: 'warning', message: 'Using offline data for exhibition types' }
            });
          }
        }
      }
    };

    fetchExhibitionTypes();

    return () => {
      isMounted = false;
    };
  }, []);

  // Fetch ticket types
  useEffect(() => {
    let isMounted = true;
    let retryCount = 0;
    const maxRetries = 2;

    const fetchTicketTypes = async () => {
      if (!isMounted || retryCount >= maxRetries) {
        if (retryCount >= maxRetries && isMounted) {
          setTicketTypeOptions([
            { id: 1, name: 'Regular Ticket' },
            { id: 2, name: 'VIP Ticket' },
            { id: 3, name: 'Group Ticket' },
            { id: 4, name: 'Student Ticket' }
          ]);
          setLoadingTicketTypes(false);
        }
        return;
      }

      setLoadingTicketTypes(true);
      try {
        const response = await mastersApi('ticket_fees', 'get', { page: 1, per_page: 20, status: "active", museum_id: museumId });
        if (isMounted) {
          // Apply additional client-side filtering for robustness
          const filteredTicketTypes = filterTicketTypesByMuseum(response?.data || []);
          setTicketTypeOptions(filteredTicketTypes);
          setLoadingTicketTypes(false);
        }
      } catch (error) {
        console.error('Error fetching ticket types:', error);
        retryCount++;

        if (retryCount < maxRetries) {
          setTimeout(() => fetchTicketTypes(), 2000 * retryCount); // Exponential backoff
        } else {
          if (isMounted) {
            setTicketTypeOptions([
              { id: 1, name: 'Regular Ticket' },
              { id: 2, name: 'VIP Ticket' },
              { id: 3, name: 'Group Ticket' },
              { id: 4, name: 'Student Ticket' }
            ]);
            setLoadingTicketTypes(false);
            dispatch({
              type: SNACKBAR_OPEN,
              payload: { isNotify: true, severity: 'warning', message: 'Using offline data for ticket types' }
            });
          }
        }
      }
    };

    fetchTicketTypes();

    return () => {
      isMounted = false;
    };
  }, []);

  // Fetch visitor types
  useEffect(() => {
    let isMounted = true;
    
    const fetchVisitorTypes = async () => {
      try {
        const res = await visitorTypes("get", null, { status: "active" });
        if (isMounted) {
          setVisitorTypeOptions(res?.data || []);
        }
      } catch (err) {
        console.error("Failed to load visitor types:", err);
        if (isMounted) {
          setVisitorTypeOptions([]);
        }
      }
    };

    fetchVisitorTypes();

    return () => {
      isMounted = false;
    };
  }, []);

  const handleGuideCidLookup = async (cid, setFieldValue) => {
    if (!cid || cid.length < 10) return;

    try {
      const response = await getTourGuideByCid(cid);
      if (response?.data?.users?.user?.length > 0) {
        const guide = response.data.users.user[0];
        setFieldValue('guide_name', guide.name || '');
        setFieldValue('guide_phone', guide.mobileNo || '');
        dispatch({
          type: SNACKBAR_OPEN,
          payload: { isNotify: true, severity: 'success', message: 'Guide information found!' }
        });
      }
    } catch (error) {
      dispatch({
        type: SNACKBAR_OPEN,
        payload: { isNotify: true, severity: 'error', message: 'Guide not found with this CID' }
      });
    }
  };

  const handleTourOperatorSearch = async (licenseNo, setFieldValue) => {
    if (!licenseNo) return;

    try {
      const response = await getTourOperatorByLicense(licenseNo);
      if (response?.data) {
        const operator = response.data;
        setFieldValue('guide_agent_name', operator.pro_Estb_Name || '');
        dispatch({
          type: SNACKBAR_OPEN,
          payload: { isNotify: true, severity: 'success', message: 'Tour operator found!' }
        });
      }
    } catch (error) {
      dispatch({
        type: SNACKBAR_OPEN,
        payload: { isNotify: true, severity: 'error', message: 'Tour operator not found with this license' }
      });
    }
  };

  const fetchTicketDiscounts = async () => {
    try {
      // Fetch discounts with proper filtering parameters
      const currentMuseumTypeId = getCurrentMuseumTypeId();
      console.log('Temporary Exhibition: Fetching discounts for museum_type_id:', currentMuseumTypeId);
      
      const response = await getTicketDiscounts({ 
        page: 1, 
        per_page: 50,
        museum_type_id: currentMuseumTypeId,
        status: 'active'
      });
      
      console.log('Temporary Exhibition: Discounts API response:', response);
      
      if (response?.data) {
        setTicketDiscounts(response.data);
        console.log('Temporary Exhibition: Set discounts:', response.data.length, 'items');
      } else {
        console.log('Temporary Exhibition: No ticket discounts data found');
        setTicketDiscounts([]);
      }
    } catch (error) {
      console.error('Temporary Exhibition: Failed to fetch ticket discounts:', error);
      setTicketDiscounts([]);
    }
  };

const fetchTicketTypes = async () => {
  try {
    const response = await mastersApi('ticket_fees', 'get', { page: 1, per_page: 20, status: "active", museum_id: museumId });
    const filteredTicketTypes = filterTicketTypesByMuseum(response?.data || []);
    setTicketTypeOptions(filteredTicketTypes);
    setLoadingTicketTypes(false);
  } catch (error) {
    console.error('Error fetching ticket types:', error);
    setTicketTypeOptions([]);
    setLoadingTicketTypes(false);
  }
};

const calculatePayableAmount = (values) => {
  let baseAmount = 0;
  let discountAmount = 0;

  // Get base fee from selected visitor type (matching backend logic)
  const selectedVisitorType = visitorTypeOptions.find(type =>
    String(type.id) === String(values.visitor_type_id)
  );

  // Backend uses ticket_fee.fee, so we need to find the matching ticket fee
  // Find ticket fee that matches the selected visitor type
  const matchingTicketFee = ticketTypeOptions.find(ticketFee =>
    String(ticketFee.visitor_type?.id) === String(values.visitor_type_id)
  );

  // Use ticket_fee.fee like the backend does
  if (matchingTicketFee?.fee) {
    const ticketFeeAmount = parseFloat(matchingTicketFee.fee);
    const noOfVisitors = parseInt(values.no_of_visitor || 1);
    baseAmount = ticketFeeAmount * noOfVisitors;
  } else {
    console.log('Temporary Exhibition: No ticket fee found for selected visitor type');
  }

  // Find applicable discount (matching backend logic)
  const applicableDiscount = ticketDiscounts
    .filter(discount => {
      const today = new Date();
      const fromDate = new Date(discount.from_date);
      const toDate = new Date(discount.to_date);

    // Check if discount is within valid date range
    const dateValid = today >= fromDate && today <= toDate;
    
    // Backend matches by museum_type_id
    const currentMuseumTypeId = getCurrentMuseumTypeId();
    const museumTypeValid = discount.museum_type?.id === currentMuseumTypeId;
    
    // Check if discount applies to the specific ticket fee
    const ticketFeeValid = discount.ticket_fee?.id === matchingTicketFee?.id;

    console.log('Temporary Exhibition: Discount filtering check:', {
      discountName: discount.name,
      dateValid: dateValid,
      museumTypeValid: museumTypeValid,
      ticketFeeValid: ticketFeeValid,
      currentMuseumTypeId: currentMuseumTypeId,
      discountMuseumTypeId: discount.museum_type?.id,
      discountTicketFeeId: discount.ticket_fee?.id,
      matchingTicketFeeId: matchingTicketFee?.id,
      willApply: dateValid && museumTypeValid && ticketFeeValid
    });

    return dateValid && museumTypeValid && ticketFeeValid;
  })
  .sort((a, b) => parseFloat(b.discount_rate) - parseFloat(a.discount_rate))[0]; // Highest discount rate first

  if (applicableDiscount) {
    const discountRate = parseFloat(applicableDiscount.discount_rate);
    discountAmount = baseAmount * (discountRate / 100);
  } else {
    console.log('Temporary Exhibition: No applicable discount found');
  }

  const finalAmount = baseAmount - discountAmount;
  setPayableAmount(finalAmount);

  return {
    baseAmount,
    discountAmount,
    finalAmount,
    applicableDiscount
  };
};

const initialValues = {
exhibition_type_id: [],
visitor_type_id: '',
no_of_visitor: '',
current_date: new Date().toISOString().split('T')[0],
country: '',
payment_mode: 'cash',
transaction_no: '',
with_guide: 'no',
guide_name: '',
guide_phone: '',
guide_agent_name: '',
museum_id: user?.museums?.[0]?.id || '',
translations: {
  en: {
    remarks: '',
    refund_notes: ''
  }
}
};

// Fetch ticket discounts on component mount
useEffect(() => {
fetchTicketDiscounts();
}, []);

const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      // Find the selected exhibition to get its museum_id
      const selectedExhibition = exhibitionTypeOptions.find(exhibition => 
        String(exhibition.id) === String(values.exhibition_type_id)
      );
      
      // Prepare submission data with required fields
      const submissionData = {
        museum_id: museumId,
        exhibition_ids: values.exhibition_type_id || [],
        visitor_type_id: values.visitor_type_id || null,
        no_of_visitor: parseInt(values.no_of_visitor) || 0,
        current_date: values.current_date,
        country: values.country || null,
        payment_mode: values.payment_mode || 'cash',
        transaction_no: values.transaction_no || null,
        withGuide: values.with_guide,
        guide_name: values.guide_name || '',
        guide_phone: values.guide_phone || '',
        guide_agent_name: values.guide_agent_name || '',
        translations: {
          en: {
            remarks: values.translations.en.remarks,
            refund_notes: values.translations.en.refund_notes,
          }
        }
      };

      // Submit to API
      await ticketsApi('post', submissionData);

      dispatch({
        type: SNACKBAR_OPEN,
        payload: { isNotify: true, severity: 'success', message: 'Temporary exhibition ticket created successfully!' }
      });

      setShowSuccess(true);
      resetForm();
    } catch (error) {
      dispatch({
        type: SNACKBAR_OPEN,
        payload: { isNotify: true, severity: 'error', message: 'Failed to create temporary exhibition ticket' }
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageWrapper>
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" sx={{ mb: 3, fontWeight: 600 }}>
          Temporary Exhibition
        </Typography>

        <Card sx={{ p: 3 }}>
          <Formik
            initialValues={initialValues}
            onSubmit={handleSubmit}
            enableReinitialize
          >
            {({ isSubmitting, values, setFieldValue, touched, errors }) => (
              <Form>
                <Grid container spacing={3}>
                  
                  {/* First Row: Visitor Type, No. of Visitors, Exhibition Type */}
                  <Grid item xs={12} sm={4}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Visitor Type</InputLabel>
                      <Select
                        value={values.visitor_type_id}
                        label="Visitor Type"
                        onChange={(e) => setFieldValue('visitor_type_id', e.target.value)}
                      >
                        <MenuItem value="">Select Visitor Type</MenuItem>
                        {visitorTypeOptions.map((type) => (
                          <MenuItem key={type.id} value={type.id}>
                            {type.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <Field
                      as={TextField}
                      name="no_of_visitor"
                      label="No. of Visitors"
                      type="number"
                      fullWidth
                      size="small"
                      InputProps={{ inputProps: { min: 1 } }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Exhibition Type</InputLabel>
                      <Select
                        multiple
                        value={values.exhibition_type_id}
                        label="Exhibition Type"
                        input={<OutlinedInput label="Exhibition Type" />}
                        onChange={(e) => setFieldValue('exhibition_type_id', e.target.value)}
                        disabled={loadingExhibitionTypes}
                        renderValue={(selected) => (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {selected.map((id) => {
                              const match = exhibitionTypeOptions.find((t) => t.id === id);
                              return <Chip key={id} label={match?.name || id} size="small" />;
                            })}
                          </Box>
                        )}
                      >
                        {loadingExhibitionTypes ? (
                          <MenuItem disabled>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <CircularProgress size={16} />
                              Loading...
                            </Box>
                          </MenuItem>
                        ) : (
                          exhibitionTypeOptions.map((type) => (
                            <MenuItem key={type.id} value={type.id}>
                              {type.name}
                            </MenuItem>
                          ))
                        )}
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Second Row: Current Date, Country, Payment Mode */}
                  <Grid item xs={12} sm={4}>
                    <Field
                      component={TextField}
                      name="current_date"
                      label="Current Date"
                      type="date"
                      fullWidth
                      size="small"
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Select Country</InputLabel>
                      <Select
                        value={values.country}
                        label="Select Country"
                        onChange={(e) => setFieldValue('country', e.target.value)}
                      >
                        <MenuItem value="">Select Country</MenuItem>
                        {countries.map((country) => (
                          <MenuItem key={country.value} value={country.value}>
                            {country.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Payment Mode */}
                  <Grid item xs={12} sm={4}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Payment Mode</InputLabel>
                      <Select
                        value={values.payment_mode}
                        label="Payment Mode"
                        onChange={(e) => setFieldValue('payment_mode', e.target.value)}
                      >
                        <MenuItem value="cash">Cash</MenuItem>
                        <MenuItem value="electronic">Electronic</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Transaction Number (conditional) */}
                  {values.payment_mode === 'electronic' && (
                    <Grid item xs={12} sm={4}>
                      <Field
                        component={TextField}
                        name="transaction_no"
                        label="Transaction Number"
                        fullWidth
                        size="small"
                        required
                      />
                    </Grid>
                  )}

                  {/* With Guide / Without Guide */}
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" sx={{ mb: 1 }}>
                      Guide Option
                    </Typography>
                    <FormControl component="fieldset">
                      <RadioGroup
                        row
                        name="with_guide"
                        value={values.with_guide}
                        onChange={(e) => setFieldValue('with_guide', e.target.value)}
                      >
                        <FormControlLabel value="yes" control={<Radio />} label="With Guide" />
                        <FormControlLabel value="no" control={<Radio />} label="Without Guide" />
                      </RadioGroup>
                    </FormControl>
                  </Grid>

                  {/* Guide Fields */}
                  <Grid container spacing={3} sx={{ ml: 1 }}>
                    {values.with_guide === 'yes' && (
                      <Grid item xs={12} sm={6}>
                        <Field
                          component={TextField}
                          variant="outlined"
                          size="small"
                          fullWidth
                          label="Guide CID"
                          name="guide_cid"
                          value={guideCid}
                          onChange={(e) => {
                            const value = e.target.value;
                            setGuideCid(value);
                            
                            // Automatically lookup when 11 digits are entered
                            if (value.length === 11) {
                              console.log('Temporary Exhibition: Auto-triggering CID lookup for 11 digits:', value);
                              handleGuideCidLookup(value, setFieldValue);
                            }
                          }}
                        />
                      </Grid>
                    )}

                    {values.with_guide === 'yes' ? (
                      <>
                        <Grid item xs={12} sm={6}>
                          <Field
                            as={TextField}
                            variant="outlined"
                            size="small"
                            fullWidth
                            label="Guide Name"
                            name="guide_name"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Field
                            as={TextField}
                            variant="outlined"
                            size="small"
                            fullWidth
                            label="Guide Phone No"
                            name="guide_phone"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Field
                            as={TextField}
                            variant="outlined"
                            size="small"
                            fullWidth
                            label="Guide Agent Name"
                            name="guide_agent_name"
                          />
                        </Grid>
                      </>
                    ) : (
                      <>
                        <Grid item xs={12} sm={6}>
                          <Field
                            as={TextField}
                            variant="outlined"
                            size="small"
                            fullWidth
                            label="Name"
                            name="guide_name"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Field
                            as={TextField}
                            variant="outlined"
                            size="small"
                            fullWidth
                            label="Contact"
                            name="guide_phone"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Field
                            as={TextField}
                            variant="outlined"
                            size="small"
                            fullWidth
                            label="Place"
                            name="guide_agent_name"
                          />
                        </Grid>
                      </>
                    )}
                  </Grid>

                  {/* Payable Amount Display */}
                  <Grid item xs={12}>
                    <Card sx={{ p: 2, mt: 2, backgroundColor: '#f5f5f5' }}>
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                        Payable Amount
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={4}>
                          <Typography variant="body2" color="text.secondary">
                            Base Amount:
                          </Typography>
                          <Typography variant="h6">
                            {(() => {
                              console.log('Temporary Exhibition Display: Values object DEBUG:', {
                                allValues: values,
                                no_of_visitor_specific: values.no_of_visitor,
                                no_of_visitor_type: typeof values.no_of_visitor,
                                visitor_type_id: values.visitor_type_id
                              });

                              const selectedVisitorType = visitorTypeOptions.find(type =>
                                String(type.id) === String(values.visitor_type_id)
                              );
                              const matchingTicketFee = ticketTypeOptions.find(ticketFee =>
                                String(ticketFee.visitor_type?.id) === String(values.visitor_type_id)
                              );
                              const ticketFeeAmount = matchingTicketFee?.fee ? parseFloat(matchingTicketFee.fee) : 0;
                              const noOfVisitors = parseInt(values.no_of_visitor || 1);
                              const baseAmount = ticketFeeAmount * noOfVisitors;

                              console.log('Temporary Exhibition Display: Base amount calculation DEBUG:', {
                                selectedVisitorType: selectedVisitorType?.name,
                                ticketFeeRaw: matchingTicketFee?.fee,
                                ticketFeeParsed: ticketFeeAmount,
                                noOfVisitorsRaw: values.no_of_visitor,
                                noOfVisitorsParsed: noOfVisitors,
                                multiplication: `${ticketFeeAmount} × ${noOfVisitors} = ${baseAmount}`,
                                baseAmount: baseAmount
                              });
                              return `Nu ${baseAmount.toFixed(2)}`;
                            })()}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <Typography variant="body2" color="text.secondary">
                            Discount:
                          </Typography>
                          <Typography variant="h6" color="error.main">
                            {(() => {
                              // Use the same logic as calculatePayableAmount function
                              const selectedVisitorType = visitorTypeOptions.find(type =>
                                String(type.id) === String(values.visitor_type_id)
                              );
                              const matchingTicketFee = ticketTypeOptions.find(ticketFee =>
                                String(ticketFee.visitor_type?.id) === String(values.visitor_type_id)
                              );
                              const ticketFeeAmount = matchingTicketFee?.fee ? parseFloat(matchingTicketFee.fee) : 0;
                              const noOfVisitors = parseInt(values.no_of_visitor || 1);
                              const baseAmount = ticketFeeAmount * noOfVisitors;

                              console.log('Temporary Exhibition Display: Discount filtering debug:', {
                                allDiscounts: ticketDiscounts.map(d => ({
                                  name: d.name,
                                  museumExhibitionTypeId: d.museum_exhibition_type?.id,
                                  museumExhibitionTypeName: d.museum_exhibition_type?.name,
                                  fromDate: d.from_date,
                                  toDate: d.to_date,
                                  discountRate: d.discount_rate
                                })),
                                selectedVisitorMuseumType: selectedVisitorType?.museum_type,
                                selectedVisitorMuseumTypeId: selectedVisitorType?.museum_type?.id,
                                ticketDiscountsLength: ticketDiscounts.length
                              });

                              const applicableDiscount = ticketDiscounts
                                .filter(discount => {
                                  const today = new Date();
                                  const fromDate = new Date(discount.from_date);
                                  const toDate = new Date(discount.to_date);

                                  // Check if discount is within valid date range
                                  const dateValid = today >= fromDate && today <= toDate;
                                  
                                  // Backend matches by museum_type_id
                                  const currentMuseumTypeId = getCurrentMuseumTypeId();
                                  const museumTypeValid = discount.museum_type?.id === currentMuseumTypeId;
                                  
                                  // Backend matches by ticket_fee_id directly
                                  const ticketFeeValid = discount.ticket_fee?.id === matchingTicketFee?.id;

                                  console.log('Temporary Exhibition Display: Checking individual discount:', {
                                    discountName: discount.name,
                                    discountMuseumTypeName: discount.museum_type?.name,
                                    selectedVisitorTypeId: selectedVisitorType?.id,
                                    discountTicketFeeId: discount.ticket_fee?.id,
                                    matchingTicketFeeId: matchingTicketFee?.id,
                                    today: today.toISOString().split('T')[0],
                                    fromDate: discount.from_date,
                                    toDate: discount.to_date,
                                    dateValid: dateValid,
                                    museumTypeValid: museumTypeValid,
                                    ticketFeeValid: ticketFeeValid,
                                    discountRate: discount.discount_rate,
                                    willApply: dateValid && museumTypeValid && ticketFeeValid
                                  });

                                  return dateValid && museumTypeValid && ticketFeeValid;
                                })
                                .sort((a, b) => parseFloat(b.discount_rate) - parseFloat(a.discount_rate))[0];

                              const discountAmount = applicableDiscount
                                ? baseAmount * (parseFloat(applicableDiscount.discount_rate) / 100)
                                : 0;

                              console.log('Temporary Exhibition Display: Discount amount calculation:', {
                                baseAmount: baseAmount,
                                applicableDiscount: applicableDiscount?.name,
                                discountRate: applicableDiscount?.discountRate,
                                discountAmount: discountAmount
                              });

                              return `-Nu ${discountAmount.toFixed(2)}`;
                            })()}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <Typography variant="body2" color="text.secondary">
                            Total Payable:
                          </Typography>
                          <Typography variant="h5" color="primary.main" sx={{ fontWeight: 'bold' }}>
                            Nu {(() => {
                              const selectedVisitorType = visitorTypeOptions.find(type =>
                                String(type.id) === String(values.visitor_type_id)
                              );
                              const matchingTicketFee = ticketTypeOptions.find(ticketFee =>
                                String(ticketFee.visitor_type?.id) === String(values.visitor_type_id)
                              );
                              const ticketFeeAmount = matchingTicketFee?.fee ? parseFloat(matchingTicketFee.fee) : 0;
                              const noOfVisitors = parseInt(values.no_of_visitor || 1);
                              const baseAmount = ticketFeeAmount * noOfVisitors;

                              console.log('Temporary Exhibition Display: Final amount calculation DEBUG:', {
                                selectedVisitorType: selectedVisitorType?.name,
                                ticketFeeRaw: matchingTicketFee?.fee,
                                ticketFeeParsed: ticketFeeAmount,
                                noOfVisitorsRaw: values.no_of_visitor,
                                noOfVisitorsParsed: noOfVisitors,
                                multiplication: `${ticketFeeAmount} × ${noOfVisitors} = ${baseAmount}`,
                                baseAmount: baseAmount
                              });

                              const applicableDiscount = ticketDiscounts
                                .filter(discount => {
                                  const today = new Date();
                                  const fromDate = new Date(discount.from_date);
                                  const toDate = new Date(discount.to_date);

                                  // Check if discount is within valid date range
                                  const dateValid = today >= fromDate && today <= toDate;
                                  
                                  // Backend matches by museum_type_id
                                  const currentMuseumTypeId = getCurrentMuseumTypeId();
                                  const museumTypeValid = discount.museum_type?.id === currentMuseumTypeId;
                                  
                                  // Backend matches by ticket_fee_id directly
                                  const ticketFeeValid = discount.ticket_fee?.id === matchingTicketFee?.id;

                                  return dateValid && museumTypeValid && ticketFeeValid;
                                })
                                .sort((a, b) => parseFloat(b.discount_rate) - parseFloat(a.discount_rate))[0];

                              const discountAmount = applicableDiscount
                                ? baseAmount * (parseFloat(applicableDiscount.discount_rate) / 100)
                                : 0;
                              const finalAmount = baseAmount - discountAmount;

                              console.log('Temporary Exhibition Display: Final amount calculation:', {
                                baseAmount: baseAmount,
                                discountAmount: discountAmount,
                                finalAmount: finalAmount,
                                applicableDiscount: applicableDiscount?.name || 'None'
                              });

                              return finalAmount.toFixed(2);
                            })()}
                          </Typography>
                        </Grid>
                      </Grid>
                      {(() => {
                        const selectedVisitorType = visitorTypeOptions.find(type =>
                          String(type.id) === String(values.visitor_type_id)
                        );

                        const applicableDiscount = ticketDiscounts.find(discount => {
                          const today = new Date();
                          const fromDate = new Date(discount.from_date);
                          const toDate = new Date(discount.to_date);

                          return (
                            today >= fromDate &&
                            today <= toDate
                          );
                        });

                        console.log('Temporary Exhibition Display: Discount info display check:', {
                          applicableDiscount: applicableDiscount?.name || 'None',
                          selectedVisitorMuseumTypeId: selectedVisitorType?.museum_type?.id,
                          today: new Date().toISOString().split('T')[0]
                        });

                        // return applicableDiscount ? (
                        //   <Box sx={{ mt: 2, p: 1, backgroundColor: '#e8f5e8', borderRadius: 1 }}>
                        //     <Typography variant="body2" color="success.main">
                        //       Discount Applied: {applicableDiscount.name} ({applicableDiscount.discount_rate}% off)
                        //     </Typography>
                        //     <Typography variant="caption" color="text.secondary">
                        //       Valid from {applicableDiscount.from_date} to {applicableDiscount.to_date}
                        //     </Typography>
                        //   </Box>
                        // ) : null;
                      })()}
                    </Card>
                  </Grid>

                  {/* Hidden fields for API requirements */}
                  <Field type="hidden" name="museum_id" />
                  <Field type="hidden" name="translations.en.remarks" />
                  <Field type="hidden" name="translations.en.refund_notes" />

                  {/* Submit Button */}
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                      <Button
                        type="submit"
                        variant="contained"
                        disabled={isSubmitting}
                        sx={{ minWidth: 120 }}
                      >
                        {isSubmitting ? 'Creating...' : 'Create Ticket'}
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </Form>
            )}
          </Formik>
        </Card>
      </Box>
    </PageWrapper>
  );
};

export default TemporaryExhibition;
